import React, { FC, useEffect, useState } from 'react'
import { Box, Text, HStack, useRadioGroup } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { RadioButtonProps } from '../radio-buttons/interfaces'
import {
  previousStep,
  selectAnswers,
  selectCurrentAnswers,
  selectScenarioAdditionalInformation,
  selectScenarioIsWaitingForAnswer,
  selectScenarioType,
} from '../../state'
import { ScenarioType, Steps } from '../../state/interfaces'
import { useDispatch, useSelector } from 'react-redux'
import { getCookie } from '../../scenarios'
import { LoadingNewAnswer } from '../spinners/loading-new-answer'

interface Choice {
  value: string
  label: string
  image?: string
  isChecked?: boolean
}

export interface RadioButtonGroupProps {
  name: string
  choices: Choice[]
  value?: string
  onChange?: (values: string[]) => void
  component: FC<RadioButtonProps>
}

export const newProviderVerification: FC<RadioButtonGroupProps> = ({
  choices,
  value,
  onChange,
  name,
  component: Component,
}) => {
  const currentAnswers = useSelector(selectCurrentAnswers)
  const scenarioIsWaitingForAnswer = useSelector(selectScenarioIsWaitingForAnswer)
  let isDeal = 'false'

  if (currentAnswers[0]) {
    isDeal = currentAnswers[0]
  }

  const { getRadioProps, getRootProps } = useRadioGroup({
    onChange: (value) => onChange && onChange([value, isDeal]),
    value,
    name,
  })

  const [providerAvailable, setProviderAvailable] = useState(true)
  const [leadProvider, setLeadProvider] = useState('')
  const answers = useSelector(selectAnswers)
  const scenarioType: ScenarioType = useSelector(selectScenarioType) as ScenarioType
  const scenarioAdditionalInformation = useSelector(selectScenarioAdditionalInformation)
  const providerName: string | undefined = getCookie('providerName')
  const dispatch = useDispatch()

  useEffect(() => {
    if (scenarioAdditionalInformation!.providerSlug) {
      setLeadProvider(scenarioAdditionalInformation!.providerSlug as string)
    }
  }, [scenarioAdditionalInformation])

  useEffect(() => {
    if (scenarioType === ScenarioType.LEAD_INTERNET) {
      if (answers[Steps.CURRENT_PROVIDER_INTERNET_LEAD] && answers[Steps.CURRENT_PROVIDER_INTERNET_LEAD]![0]) {
        if (leadProvider !== answers[Steps.CURRENT_PROVIDER_INTERNET_LEAD]![0]) {
          setProviderAvailable(true)
        } else {
          setProviderAvailable(false)
        }
      }
    }

    if (scenarioType === ScenarioType.LEAD_MOBILE) {
      if (answers[Steps.CURRENT_PROVIDER_MOBILE_LEAD] && answers[Steps.CURRENT_PROVIDER_MOBILE_LEAD]![0]) {
        if (leadProvider !== answers[Steps.CURRENT_PROVIDER_MOBILE_LEAD]![0]) {
          setProviderAvailable(true)
        } else {
          setProviderAvailable(false)
        }
      }
    }
  }, [answers])

  const { t } = useTranslation()

  return (
    <Box>
      {providerAvailable ? (
        <Box textAlign={'center'}>
          <Text fontSize="xs" fontWeight="bold" color="error" textTransform="uppercase" marginBottom={2}>
            {t('you-are-eligible')}
          </Text>
        </Box>
      ) : (
        <Box textAlign={'center'}>
          <Text fontSize="xs" fontWeight="bold" color="error" textTransform="uppercase" marginBottom={2}>
            {t('sorry')}
          </Text>
          <Text fontSize="18" marginBottom={2}>
            {t('plan-not-available-for-new-customers', { providerName })}
          </Text>
          <Text fontSize="16" marginBottom={2} fontStyle="italic">
            {t('showing-similar-plans', { providerName })}
          </Text>
        </Box>
      )}

      <HStack {...getRootProps()} justifyContent="center" margin="2vh">
        {scenarioIsWaitingForAnswer ? (
          <LoadingNewAnswer />
        ) : (
          choices.map((props) => (
            <Component key={`choice-${props.value}`} {...getRadioProps({ value: props.value })} {...props} />
          ))
        )}
      </HStack>

      <Box textAlign={'center'} marginTop={4} textColor="brand">
        <button
          onClick={() => dispatch(previousStep())}
          style={{ textDecoration: 'none' }}
          onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
        >
          {t('change-provider')}
        </button>
      </Box>
    </Box>
  )
}
